import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { worksList, workTypes, workYears, roleTypes } from "@/mocks/works";

const PAGE_SIZE = 8;

type SortKey = "yearDesc" | "yearAsc" | "rating";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "yearDesc", label: "上映时间（新→老）" },
  { key: "yearAsc", label: "上映时间（老→新）" },
  { key: "rating", label: "评分最高" },
];

const ALL = "全部";

export default function WorksListPage() {
  const [type, setType] = useState<string>(ALL);
  const [year, setYear] = useState<string>(ALL);
  const [role, setRole] = useState<string>(ALL);
  const [sort, setSort] = useState<SortKey>("yearDesc");
  const [page, setPage] = useState(1);
  const [panelOpen, setPanelOpen] = useState(false);

  const filtered = useMemo(() => {
    const result = worksList.filter(
      (item) =>
        (type === ALL || item.type === type) &&
        (year === ALL || item.year === Number(year)) &&
        (role === ALL || item.roleType === role)
    );
    if (sort === "yearDesc") result.sort((a, b) => b.year - a.year);
    if (sort === "yearAsc") result.sort((a, b) => a.year - b.year);
    if (sort === "rating") result.sort((a, b) => b.rating - a.rating);
    return result;
  }, [type, year, role, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setType(ALL);
    setYear(ALL);
    setRole(ALL);
    setSort("yearDesc");
    setPage(1);
    setPanelOpen(false);
  };

  const selectClass =
    "text-sm font-body bg-background-100 border border-background-200 rounded-md px-3 py-2 text-foreground-800 focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer";

  return (
    <SiteLayout
      title="白石茉莉奈影视作品全集 - 明星动态平台"
      description="白石茉莉奈影视作品全集，按电影、电视剧、上映年份与角色类型筛选，查看每部作品的简介与详细资料。"
      keywords="白石茉莉奈作品,白石茉莉奈影视作品,作品全集,电影,电视剧"
    >
      <div className="px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "作品" }]} />
        <div className="mt-5 md:mt-7">
          <span className="text-xs font-body tracking-[0.3em] text-accent-600">
            FILMOGRAPHY
          </span>
          <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mt-2 tracking-wide">
            白石茉莉奈影视作品全集
          </h1>
          <p className="text-sm md:text-base text-foreground-500 mt-3 max-w-2xl font-body">
            收录她参与的电影与电视剧作品，支持按类型、年份与角色类型筛选，点击海报查看作品详情。
          </p>
        </div>
      </div>

      <div className="px-4 md:px-8 py-8 md:py-10">
        <div className="hidden md:flex flex-wrap items-center gap-3 mb-8 pb-5 border-b border-background-200">
          <select
            value={type}
            onChange={(e) => {
              setType(e.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value={ALL}>全部类型</option>
            {workTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>

          <select
            value={year}
            onChange={(e) => {
              setYear(e.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value={ALL}>全部年份</option>
            {workYears.map((y) => (
              <option key={y} value={String(y)}>
                {y} 年
              </option>
            ))}
          </select>

          <select
            value={role}
            onChange={(e) => {
              setRole(e.target.value);
              setPage(1);
            }}
            className={selectClass}
          >
            <option value={ALL}>全部角色类型</option>
            {roleTypes.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>

          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-foreground-400 font-body">排序</span>
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as SortKey);
                setPage(1);
              }}
              className={selectClass}
            >
              {sortOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="md:hidden flex items-center justify-between gap-3 mb-5">
          <button
            type="button"
            onClick={() => setPanelOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-background-300 text-sm font-body text-foreground-700 cursor-pointer"
          >
            <i className="ri-filter-3-line"></i>
            筛选 / 排序
          </button>
          <span className="text-sm text-foreground-400 font-body">
            共 {filtered.length} 部
          </span>
        </div>

        <p className="hidden md:block text-sm text-foreground-400 font-body mb-6">
          共 {filtered.length} 部作品
        </p>

        {pageItems.length === 0 ? (
          <EmptyState
            title="没有符合条件的作品"
            description="试试调整筛选条件，或重置查看全部作品。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pageItems.map((item) => (
              <WorkCard key={item.id} item={item} />
            ))}
          </div>
        )}

        <Pagination current={safePage} total={totalPages} onChange={setPage} />
      </div>

      {panelOpen && (
        <div className="md:hidden fixed inset-0 z-[60] flex items-end">
          <div
            className="absolute inset-0 bg-foreground-950/60"
            onClick={() => setPanelOpen(false)}
          ></div>
          <div className="relative w-full bg-background-50 rounded-t-2xl p-5 pb-8 max-h-[82vh] overflow-y-auto animate-fade-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-lg text-foreground-950">
                筛选与排序
              </h3>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <p className="text-sm font-body text-foreground-500 mb-3">作品类型</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {[ALL, ...workTypes].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => {
                    setType(t);
                    setPage(1);
                  }}
                  className={`text-sm font-body px-4 py-2 rounded-full cursor-pointer whitespace-nowrap ${
                    type === t
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <p className="text-sm font-body text-foreground-500 mb-3">上映年份</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {[ALL, ...workYears.map(String)].map((y) => (
                <button
                  key={y}
                  type="button"
                  onClick={() => {
                    setYear(y);
                    setPage(1);
                  }}
                  className={`text-sm font-body px-4 py-2 rounded-full cursor-pointer whitespace-nowrap ${
                    year === y
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600"
                  }`}
                >
                  {y === ALL ? "全部" : `${y} 年`}
                </button>
              ))}
            </div>

            <p className="text-sm font-body text-foreground-500 mb-3">角色类型</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {[ALL, ...roleTypes].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    setRole(r);
                    setPage(1);
                  }}
                  className={`text-sm font-body px-4 py-2 rounded-full cursor-pointer whitespace-nowrap ${
                    role === r
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>

            <p className="text-sm font-body text-foreground-500 mb-3">排序</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {sortOptions.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => {
                    setSort(opt.key);
                    setPage(1);
                  }}
                  className={`text-sm font-body px-4 py-2 rounded-full cursor-pointer whitespace-nowrap ${
                    sort === opt.key
                      ? "bg-secondary-500 text-background-50"
                      : "bg-background-100 text-foreground-600"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="flex-1 py-3 rounded-md border border-background-300 text-sm font-body text-foreground-700 cursor-pointer"
              >
                重置
              </button>
              <button
                type="button"
                onClick={() => setPanelOpen(false)}
                className="flex-1 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-body cursor-pointer"
              >
                查看结果
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}